import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { Input, LoaderCircle, Table } from '@skytech/ui'
import { Th } from '@skytech/ui/Table/Th'
import { getSortedList, useDebounce } from '@skytech/utils'
import { getQueryFilter } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { LoadingError } from '@/components/LoadingError'
import { NoResults } from '@/components/NoResults'
import { useItPurchaserAccess } from '@/hooks/access'
import { useSearchParamsByKey, useSortParams } from '@/hooks/searchParams'
import { useRenderLimit } from '@/hooks/use-render-limit'
import queries from '@/modules/admin/queries'
import adminResources from '@/modules/admin/resources'
import type { Asset } from '@/modules/admin/resources'

import { AssetListItem } from './AssetListItem'

import './Assets.styles.scss'

export const Assets: FC = () => {
  useItPurchaserAccess()

  const translate = useTranslation()
  const { locale } = useAppContext()
  const params = useParams()

  const costObjectId = Number(params.costObjectId)

  if (isNaN(costObjectId)) throw Error(translate('Employee ID is not a number!'))

  const { data, isError } = useQuery(queries.adminAssetList({ allAssets: true }), () =>
    adminResources.getAssetListWithFilters({ allAssets: true })
  )

  const [querySearch, setSearchQuery] = useSearchParamsByKey('asset-search')
  const [searchFilter, setSearchFilter] = useState(querySearch || '')
  const debouncedAssetSearchTerm = useDebounce(searchFilter, 500)
  const [{ querySort, querySortBy }, { handleSort }] = useSortParams<keyof Asset>()

  useEffect(() => {
    setSearchFilter(querySearch || '')
  }, [querySearch])

  useEffect(() => {
    setSearchQuery(debouncedAssetSearchTerm)
  }, [debouncedAssetSearchTerm, setSearchQuery])

  const stringMatcher = (item: Asset) => [
    item.assetDescription,
    item.costObjectId,
    item.costObjectName,
    item.customerName,
    item.imeiSnr,
    translate(`asset_type/${item.assetType}`),
    translate(`asset_user_type/${item.userType}`),
    translate(`asset_status/${item.status}`),
    item.address,
    item.addressZip,
    item.addressCity,
    item.costObjectCode,
  ]
  const itemsFiltered =
    data?.assets.filter(
      asset =>
        asset.costObjectId === costObjectId && getQueryFilter(searchFilter, stringMatcher)(asset)
    ) || []
  const sortedList = getSortedList({ list: itemsFiltered, querySort, querySortBy, locale })
  const { visibleList: visibleListAssets, ListLoaderComponent } = useRenderLimit(
    sortedList || [],
    30
  )

  if (isError) return <LoadingError />
  if (!data) return <LoaderCircle />

  return (
    <div className="cost-object-assets">
      <div className="table-actions">
        <div className="search">
          <Input
            id="employee-asset-search"
            type="text"
            value={searchFilter}
            placeholder={translate('Search')}
            onChange={e => setSearchFilter(e.target.value)}
          />
        </div>
      </div>

      {itemsFiltered.length > 0 ? (
        <div className="table-container">
          <Table rowHeight="tall">
            <thead>
              <tr>
                <Th className="hidden w-24 md:table-cell" />

                <Th id="assetDescription" onClick={handleSort}>
                  <Translation id="name" />
                </Th>

                <Th className="hidden md:table-cell" id="costObjectName" onClick={handleSort}>
                  <Translation id="employee" />
                </Th>

                <Th id="status" className="hidden md:table-cell" onClick={handleSort}>
                  <Translation id="status" />
                </Th>

                <Th className="hidden md:table-cell" id="address" onClick={handleSort}>
                  <Translation id="address" />
                </Th>

                <Th className="hidden md:table-cell" id="received" onClick={handleSort}>
                  <Translation id="received" />
                </Th>

                <Th className="hidden md:table-cell" id="cost" onClick={handleSort}>
                  <Translation id="cost" />
                </Th>
              </tr>
            </thead>

            <tbody>
              {visibleListAssets.map(item => (
                <AssetListItem key={item.id} searchFilter={searchFilter} {...item} />
              ))}
            </tbody>
          </Table>
          <ListLoaderComponent />
        </div>
      ) : (
        <NoResults />
      )}
    </div>
  )
}
